export const CLIENT_ACK = 'client:ok';
export const DEVICE_ACK = 'device:ok';

export enum XrayRtcConnectionEvent {
  //
  // Data Channel
  //
  clientDataChannelClosed = 'clientDataChannelClosed',
  clientDataChannelCreated = 'clientDataChannelCreated',
  clientDataChannelError = 'clientDataChannelError',
  clientDataChannelOpened = 'clientDataChannelOpened',
  deviceDataChannelClosed = 'deviceDataChannelClosed',
  deviceDataChannelCreated = 'deviceDataChannelCreated',
  deviceDataChannelError = 'deviceDataChannelError',
  deviceDataChannelOpened = 'deviceDataChannelOpened',
  //
  // Ice Agent
  //
  iceAgentCheckingPairs = 'iceAgentCheckingPairs',
  iceAgentClosed = 'iceAgentClosed',
  iceAgentGatheringCompleted = 'iceAgentGatheringCompleted',
  iceAgentPairsConnectedButStillChecking = 'iceAgentPairsConnectedButStillChecking',
  iceAgentDisconnected = 'iceAgentDisconnected',
  iceAgentFailed = 'iceAgentFailed',
  iceAgentInitialized = 'iceAgentInitialized',
  //
  // Ice Candidate
  //
  clientIceCandidateSent = 'clientIceCandidateSent',
  clientIceCandidateGatheringError = 'clientIceCandidateGatheringError',
  clientIceCandidateGatheringInitiated = 'clientIceCandidateGatheringInitiated',
  clientIceCandidateGatheringInProgress = 'clientIceCandidateGatheringInProgress',
  clientIceCandidateGatheringComplete = 'clientIceCandidateGatheringComplete',
  deviceIceCandidateAdded = 'deviceIceCandidateAdded',
  deviceIceCandidateAddError = 'deviceIceCandidateAddError',
  //
  // Local Description
  //
  localDescriptionError = 'localDescriptionError',
  localDescriptionSet = 'localDescriptionSet',
  //
  // Offer
  //
  offerCreateError = 'offerCreateError',
  offerCreated = 'offerCreated',
  offerSent = 'offerSent',
  //
  // Peer Connection
  //
  peerConnectionClosed = 'peerConnectionClosed',
  peerConnectionConnected = 'peerConnectionConnected',
  peerConnectionConnecting = 'peerConnectionConnecting',
  peerConnectionCreated = 'peerConnectionCreated',
  peerConnectionDisconnected = 'peerConnectionDisconnected',
  peerConnectionError = 'peerConnectionError',
  //
  // Remote Description
  //
  remoteDescriptionAnswerCreated = 'remoteDescriptionAnswerCreated',
  remoteDescriptionAnswerError = 'remoteDescriptionAnswerError',
  remoteDescriptionAnswerSent = 'remoteDescriptionAnswerSent',
  remoteDescriptionError = 'remoteDescriptionError',
  remoteDescriptionSet = 'remoteDescriptionSet',
  //
  // Reporter
  //
  reporterError = 'reporterError',
  //
  // Connection
  //
  connectionError = 'connectionError', // emits if any error whatsoever occurs
  connectionInstantiated = 'connectionInstantiated',
  connectionNotSupportedError = 'connectionNotSupportedError',
  connectionStart = 'connectionStart',
  connectionStop = 'connectionStop',
  //
  // Web Socket
  //
  webSocketAckReceived = 'webSocketAckReceived',
  webSocketAckSent = 'webSocketAckSent',
  webSocketClosedAbnormal = 'webSocketClosedAbnormal',
  webSocketClosedNormal = 'webSocketClosedNormal',
  webSocketError = 'webSocketError',
  webSocketCreated = 'webSocketCreated',
  webSocketOpened = 'webSocketOpened',
  webSocketTimeout = 'webSocketTimeout',
}

/**
 * Update accorrding to https://gitlab.com/masonamerica/platform/xray-service/-/blob/master/xray/requestmanager.go#L19
 */
export type XrayRtcFeature =
  | 'device-av'
  | 'file-stream'
  | 'interactive-shell'
  | 'log-stream'
  | 'remote-control'
  | 'screen-share';

export type XrayRtcOnDataChannelOpen = (dataChannel: RTCDataChannel) => void;
export type XrayRtcConnectionStatus = 'closed' | 'closing' | 'error' | 'idle' | 'pending' | 'running';

export interface XrayRtcConnectionInfo {
  bytesReceived: number;
  bytesSent: number;
  durationSeconds: number;
  megabitsPerSecond: number;
  secondsBeforeTimeout: number;
  status: XrayRtcConnectionStatus;
  resetPendingTimeout?: () => void;
}

/**
 * Options object that must be provided when instantiating a new Mason X-Ray RTC connection.
 * @param clientToken the authorization token required to create a new connection.
 * @param deviceId the ID of the remote device to connect to.
 * @param feature the X-Ray feature that this connection will support.
 * @param params the url params that this connection will support.
 * @param reportIntervalSeconds optional number of seconds which connection info will be returned via onReportInterval callback; defaults to 60 (polls/returns report once every minute).
 * @param timeoutSeconds optional number of seconds of inactivity before the onTimeout callback is called; defaults to 900 (15-minutes). NOTE that this does not stop the connection; it is the responsibility of the consumer to call the connection's stop method when the onTimeout callback is issued.
 * @param onConnectionStop optional handler this is called after a stop is initiated by consumer and all connections have been closed.
 * @param onDataChannelOpen optional handler that is called when the RTC data channel is opened and ready for communication.
 * @param onError optional handler that is called when an error occurs.
 * @param onReportInterval optional handler that is called on regular intervals, returning connection stats/information; report interval is defined by the reportIntervalSeconds property.
 * @param onTimeout optional handler that is called when the connection should be closed due to a timeout (i.e. period of inactivity). NOTE that this does not stop the connection; it is the responsibility of the consumer to call the connection's stop method when the onTimeout callback is issued.
 */
export interface XrayRtcConnectionOptions {
  clientToken: string;
  deviceId: string;
  feature: XrayRtcFeature;
  params?: Record<string, string>;
  reportIntervalSeconds?: number;
  timeoutSeconds?: number;
  onConnectionStop?: (info: XrayRtcConnectionInfo) => void;
  onDataChannelOpen?: XrayRtcOnDataChannelOpen;
  onError?: (error: string) => void;
  onReport?: (stats: XrayRtcConnectionInfo) => void;
  onTimeout?: () => void;
}
